// DocumentEditorView.jsx - REFACTORED FOR SEPARATE CODIFICATIONS COLLECTION
// Major changes:
// 1. Replaced codifiedContents state with codifications API calls
// 2. Load codifications from GET /api/codifications?documentId={id}
// 3. Save codifications via POST/PUT to /api/codifications
// 4. Removed embedded codifiedContents from document save payload
// 5. Added feature flag for gradual migration (STRANGLER FIG pattern)

import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box, Heading, HStack, VStack, IconButton, Image, Tooltip, useToast, Input,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb, Text, Select,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Button,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  Menu, MenuButton, MenuList, MenuItem,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  FormControl, FormLabel,
  useDisclosure,
} from "@chakra-ui/react";
import { SmallCloseIcon } from "@chakra-ui/icons";
import { useEditor, EditorContent, BubbleMenu } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Table from "@tiptap/extension-table";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import { Extension } from "@tiptap/core";
import Paragraph from "@tiptap/extension-paragraph";
import { Plugin, PluginKey, TextSelection } from "prosemirror-state";
import { TableMap } from "prosemirror-tables";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCursor from "@tiptap/extension-collaboration-cursor";
import * as Y from "yjs";
import { HocuspocusProvider } from "@hocuspocus/provider";
import FontSize from "../extensions/FontSize";
import Navbar from "../Navbar";
import HeroImage from "../images/stamps.png";
import { CustomTableRow } from "../extensions/CustomTableRow";
import { CustomCell } from "../extensions/CustomCell";
import { CustomTableHeader } from "../extensions/CustomTableHeader";
import MenuBar from "./MenuBar";
import PlacingProcessBar from "./PlacingProcessBar";
import QueueMonitor from "./QueueMonitor";
import {
  HiOutlineArrowNarrowLeft, HiOutlineSave, HiOutlinePlus, HiOutlineMinus, HiOutlineTrash, HiOutlineLink, HiOutlineColorSwatch, HiOutlinePrinter,
} from "react-icons/hi";
import { MdFormatColorText, MdFormatColorReset } from "react-icons/md";
import { PageBreak } from "./PageBreak";
import AutoResizeTextarea from "../AutoResizeTextarea";
import CodifyQueue from "../utils/CodifyQueue";
import ReactJson from "react-json-view";
import { useAuth } from "../AuthContext";
import { useAuthenticatedRequest } from "../hooks/useAuthenticatedRequest";
import DocumentMetadataEditor from "./DocumentMetadataEditor";
import CodesetSelect from "../CodesetSelect";
import { unwrapFetchResponse, unwrapApiResponse } from "../utils/unwrapApiResponse";
import { showApiErrorToast, showApiSuccessToast } from "../utils/showApiToast";

const DOCS_API = "https://documents.poc.mrc.ai/api/documents";
const CODIFY_API = "https://codify.poc.mrc.ai/api/codifyv3";
const CODIFICATIONS_API = "https://documents.poc.mrc.ai/api/codifications";
const CODIFY_SPECIFICATIONS_API = "https://codify.poc.mrc.ai/api/specifications";
const PLACEMENTS_API = "https://placing.poc.mrc.ai/api/placements";

function safeUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "uuid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function generateUserColor(name) {
  return '#718096';
}

const LockManagement = Extension.create({
  name: 'lockManagement',

  addProseMirrorPlugins() {
    const userRoles = this.options.userRoles || [];
    const debugMode = this.options.debugMode || false;


    const applyLocks = (state) => {
      let tr = state.tr;
      let changed = false;

      state.doc.descendants((node, pos) => {
        if (node.type.name === 'tableRow') {
          const editableBy = node.attrs?.editableBy;

          if (debugMode && editableBy && editableBy.length > 0) {
          }

          let cellOffset = 1;
          node.forEach((cell) => {
            const cellPos = pos + cellOffset;
            const cellRole = cell.attrs?.role;

            if (cellRole === 'heading' || cellRole === 'content') {
              const canEdit = editableBy && editableBy.length > 0 &&
                userRoles.some(ur => {
                  const userRoleCode = ur.role || ur;
                  const matches = editableBy.some(eb => {
                    const match = eb.toLowerCase() === userRoleCode.toLowerCase();
                    return match;
                  });
                  return matches;
                });


              const currentClass = cell.attrs?.class || '';
              const shouldHaveLock = !canEdit;
              const hasLock = currentClass.includes('locked');

              if (shouldHaveLock && !hasLock) {
                tr.setNodeMarkup(cellPos, undefined, {
                  ...cell.attrs,
                  class: currentClass ? `${currentClass} locked` : 'locked'
                });
                changed = true;
              } else if (!shouldHaveLock && hasLock) {
                tr.setNodeMarkup(cellPos, undefined, {
                  ...cell.attrs,
                  class: currentClass.replace(/\s*locked\s*/g, '').trim()
                });
                changed = true;
              }
            }

            cellOffset += cell.nodeSize;
          });
        }
        return true;
      });

      return { tr, changed };
    };

    return [
      new Plugin({
        key: new PluginKey('lockManagement'),

        view(editorView) {
          setTimeout(() => {
            const { tr, changed } = applyLocks(editorView.state);
            if (changed) {
              editorView.dispatch(tr);
            }
          }, 10);
          return {};
        },

        appendTransaction(_txs, _old, state) {
          const { tr, changed } = applyLocks(state);
          return changed ? tr : null;
        },
        
        filterTransaction(tr, state) {
          if (!tr.docChanged) return true;

          let allowed = true;

          tr.steps.forEach((step, i) => {
            if (!allowed || !step.slice) return;

            const $from = tr.docs[i].resolve(step.from);

            let cellNode = null;
            let rowNode = null;

            for (let d = $from.depth; d > 0; d--) {
              const node = $from.node(d);
              if (!cellNode && (node.type.name === 'tableCell' || node.type.name === 'tableHeader')) {
                cellNode = node;
              }
              if (!rowNode && node.type.name === 'tableRow') {
                rowNode = node;
              }
              if (cellNode && rowNode) break;
            }

            if (cellNode && rowNode) {
              const cellRole = cellNode.attrs?.role;
              const editableBy = rowNode.attrs?.editableBy;

              if (cellRole === 'heading' || cellRole === 'content') {
                const canEdit = editableBy && editableBy.length > 0 &&
                  userRoles.some(ur => {
                    const userRoleCode = ur.role || ur;
                    return editableBy.some(eb =>
                      eb.toLowerCase() === userRoleCode.toLowerCase()
                    );
                  });

                if (!canEdit) {
                  allowed = false;
                }
              }
            }
          });

          return allowed;
        },
      }),
    ];
  },
});

function findTableFromSelection(selection) {
  for (let d = selection.$from.depth; d > 0; d--) {
    const node = selection.$from.node(d);
    if (node.type?.spec?.tableRole === "table") {
      return { node, pos: selection.$from.before(d) };
    }
  }
  return null;
}

function findCellFromSelection(selection) {
  for (let d = selection.$from.depth; d > 0; d--) {
    const node = selection.$from.node(d);
    const role = node.type?.spec?.tableRole;
    if (role === "cell" || role === "header_cell") {
      return { node, pos: selection.$from.before(d) };
    }
  }
  return null;
}

const CustomTable = Table.extend({
  addExtensions() {
    return [CustomTableRow, CustomCell, CustomTableHeader];
  },
  addKeyboardShortcuts() {
    const parent = this.parent?.() ?? {};
    return {
      ...parent,
      Tab: ({ editor }) => {
        const { state } = editor;
        const tableInfo = findTableFromSelection(state.selection);
        if (!tableInfo) return parent.Tab?.({ editor }) ?? false;

        const cellInfo = findCellFromSelection(state.selection);
        if (!cellInfo) return parent.Tab?.({ editor }) ?? false;

        const map = TableMap.get(tableInfo.node);
        const tableStart = tableInfo.pos + 1;
        const rect = map.findCell(cellInfo.pos - tableStart);
        const isLastCell = rect.left === map.width - 1 && rect.top === map.height - 1;

        if (!isLastCell) return parent.Tab?.({ editor }) ?? false;

        const after = tableInfo.pos + tableInfo.node.nodeSize;
        let tr = state.tr;
        const nextNode = state.doc.nodeAt(after);
        if (!nextNode) {
          const para = state.schema.nodes.paragraph;
          if (para) tr = tr.insert(after, para.createAndFill());
        }
        const selPos = Math.min(after + 1, tr.doc.content.size);
        tr = tr.setSelection(TextSelection.create(tr.doc, selPos)).scrollIntoView();
        editor.view.dispatch(tr);
        return true;
      },
      ArrowUp: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;

        const $pos = selection.$from;
        const cellInfo = findCellFromSelection(selection);
        if (!cellInfo) return parent.ArrowUp?.({ editor }) ?? false;

        const cellStart = cellInfo.pos + 1;
        if ($pos.pos !== cellStart) return parent.ArrowUp?.({ editor }) ?? false;

        const result = parent.ArrowUp?.({ editor }) ?? false;

        setTimeout(() => {
          const newState = editor.state;
          const newSelection = newState.selection;
          const newCellInfo = findCellFromSelection(newSelection);

          if (newCellInfo) {
            const cellEnd = newCellInfo.pos + newCellInfo.node.nodeSize - 1;
            const tr = newState.tr.setSelection(TextSelection.create(newState.doc, cellEnd));
            editor.view.dispatch(tr);
          }
        }, 0);

        return result;
      },
      "Mod-a": ({ editor }) => {
        const { state } = editor;
        const cellInfo = findCellFromSelection(state.selection);

        if (!cellInfo) return parent["Mod-a"]?.({ editor }) ?? false;

        const cellStart = cellInfo.pos + 1;
        const cellEnd = cellInfo.pos + cellInfo.node.nodeSize - 1;

        const tr = state.tr.setSelection(TextSelection.create(state.doc, cellStart, cellEnd));
        editor.view.dispatch(tr);

        return true;
      },
    };
  },
});

const StableRowIds = Extension.create({
  name: "stableRowIds",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction(_txs, _old, state) {
          let tr = state.tr;
          let changed = false;
          state.doc.descendants((node, pos) => {
            if (node.type.name === "tableRow" && !node.attrs?.id) {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, id: safeUuid() });
              changed = true;
            }
            return true;
          });
          return changed ? tr : null;
        },
      }),
    ];
  },
});

export default function DocumentEditorView({
  tiptapDocument,
  contract,
  documentMeta,
  onClose,
  onSave,
  userRoles = [],
}) {
  const toast = useToast();
  const { user } = useAuth();
  const { get, post, put, delete: del, getHeaders } = useAuthenticatedRequest();
  const isSuperAdmin = user?.platformUser?.isSuperAdmin === "true";
  const [debugMode, setDebugMode] = useState(false);

  const TEST_LOCK_MODE = false;
  const effectiveUserRoles = TEST_LOCK_MODE
    ? userRoles.filter(r => r.role !== 'broker')
    : userRoles;

  const queueRef = useRef(null);
  if (!queueRef.current) {
    queueRef.current = new CodifyQueue({ concurrency: 1, maxRetries: 2, baseDelayMs: 700, jitterMs: 300 });
  }

  const ydocRef = useRef(null);
  const providerRef = useRef(null);
  const yCodifiedContentsRef = useRef(null);

  if (!ydocRef.current) {
    ydocRef.current = new Y.Doc({ gc: true, gcFilter: () => true });
    yCodifiedContentsRef.current = ydocRef.current.getMap('codifiedContents');
  }

  if (!providerRef.current && documentMeta?.documentRepositoryId) {
    const provider = new HocuspocusProvider({
      url: 'wss://studio.poc.mrc.ai/collab',
      name: documentMeta.documentRepositoryId,
      document: ydocRef.current,
      connect: true,

      onConnect: () => {
      },

      onDisconnect: ({ event }) => {
      },

      onStatus: ({ status }) => {
      },

      onSynced: ({ state }) => {
      },

      onClose: ({ event }) => {
        if (event.code !== 1000) {
          toast({
            title: 'Collaboration Disconnected',
            description: 'Attempting to reconnect...',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
        }
      },

      preserveConnection: true,
      quiet: !debugMode,
    });

    providerRef.current = provider;
  }

  // State for ALL codifications - maps participantRole -> codification
  const [codificationsByRole, setCodificationsByRole] = useState(new Map());
  const [selectedParticipantRole, setSelectedParticipantRole] = useState("Broker");

  // rowData map for CURRENT participant - maps rowId -> row codification data
  const [rowDataMap, setRowDataMap] = useState(new Map());
  const rowDataMapRef = useRef(rowDataMap);
  useEffect(() => { rowDataMapRef.current = rowDataMap; }, [rowDataMap]);

  // Helper to get current codification
  const currentCodification = codificationsByRole.get(selectedParticipantRole);
  const codificationId = currentCodification?._id || null;

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [zoom, setZoom] = useState(1.25);
  const [jsonZoom, setJsonZoom] = useState(1.0);
  const [panelWidth, setPanelWidth] = useState(22);
  const [isDragging, setIsDragging] = useState(false);
  const [expandedAccordionIndices, setExpandedAccordionIndices] = useState([]);

  const [isMetadataEditorOpen, setIsMetadataEditorOpen] = useState(false);
  const [selectedRowMetadata, setSelectedRowMetadata] = useState(null);

  const [placingStage, setPlacingStage] = useState("build");

  // Placement state - tracks the placement linked to this document
  const [placement, setPlacement] = useState(null);
  const [isLoadingPlacement, setIsLoadingPlacement] = useState(false);
  const [isCreatingPlacement, setIsCreatingPlacement] = useState(false);
  const [isDeletingPlacement, setIsDeletingPlacement] = useState(false);

  // Organization selection for placement (when user has multiple orgs)
  const { isOpen: isOrgSelectOpen, onOpen: onOrgSelectOpen, onClose: onOrgSelectClose } = useDisclosure();
  const [availableOrganisations, setAvailableOrganisations] = useState([]);
  const [organisationNames, setOrganisationNames] = useState({});
  const [selectedOrganisationId, setSelectedOrganisationId] = useState(null);

  // Auto-save state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // BubbleMenu state
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [placingStepCode, setPlacingStepCode] = useState("submission");
  const [participantRole, setParticipantRole] = useState("Broker");

  const [editableDocMeta, setEditableDocMeta] = useState({
    name: documentMeta?.name || "Untitled Document",
    codifyCollection: documentMeta?.codifyCollection || null,
    dataStandard: documentMeta?.dataStandard || null,
    dataType: documentMeta?.dataType || null,
    model: documentMeta?.model || null,
    isTemplate: documentMeta?.isTemplate ?? false,
    productCode: documentMeta?.productCode || null,
  });

  const [codifySpecifications, setCodifySpecifications] = useState(documentMeta?.codifySpecifications || []);
  const [availableSpecifications, setAvailableSpecifications] = useState([]);
  const [loadingSpecifications, setLoadingSpecifications] = useState(false);
  const [selectedSpecificationId, setSelectedSpecificationId] = useState(''); // For controlling the dropdown
  const [specificationCodifications, setSpecificationCodifications] = useState(new Map()); // Map of specId -> codified data
  const [specificationCodificationIds, setSpecificationCodificationIds] = useState(new Map()); // Map of specId -> codification _id
  const [coreDefinitions, setCoreDefinitions] = useState([]);
  const [specificationDefinitions, setSpecificationDefinitions] = useState(new Map()); // Map of specId -> definitions array
  
  // Refs to access latest definitions synchronously (avoid race conditions)
  const coreDefinitionsRef = useRef([]);
  const specificationDefinitionsRef = useRef(new Map());
  const codifySpecificationsRef = useRef([]);
  const specificationCodificationsRef = useRef(new Map());

  useEffect(() => { coreDefinitionsRef.current = coreDefinitions; }, [coreDefinitions]);
  useEffect(() => { specificationDefinitionsRef.current = specificationDefinitions; }, [specificationDefinitions]);
  useEffect(() => { codifySpecificationsRef.current = codifySpecifications; }, [codifySpecifications]);
  useEffect(() => { specificationCodificationsRef.current = specificationCodifications; }, [specificationCodifications]);

  useEffect(() => {
    const fetchSpecifications = async () => {
      setLoadingSpecifications(true);
      try {
        const response = await get(CODIFY_SPECIFICATIONS_API);
        setAvailableSpecifications(response.data);
      } catch (err) {
        console.error('Failed to fetch codify specifications:', err);
      } finally {
        setLoadingSpecifications(false);
      }
    };
    fetchSpecifications();
  }, []);

  // Fetch core definitions when codifyCollection changes
  useEffect(() => {
    const fetchCoreDefinitions = async () => {
      const collection = editableDocMeta.codifyCollection;
      if (!collection) {
        setCoreDefinitions([]);
        return;
      }

      try {
        const response = await get(`https://codify.poc.mrc.ai/api/definitions?collection=${collection}`);
        setCoreDefinitions(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to fetch core definitions:', err);
        setCoreDefinitions([]);
      }
    };
    fetchCoreDefinitions();
  }, [editableDocMeta.codifyCollection]);

  // Fetch definitions for each specification
  // This only runs on initial load when specs are loaded from saved document
  useEffect(() => {
    const fetchSpecDefinitions = async () => {
      // Start with existing definitions to preserve manually-set ones
      setSpecificationDefinitions(prev => {
        const newDefsMap = new Map(prev);

        // Only fetch for specs that DON'T have definitions yet
        const specsNeedingDefs = codifySpecifications.filter(spec => {
          const specId = spec.specificationId || spec._id || spec.id;
          return !newDefsMap.has(specId);
        });

        if (specsNeedingDefs.length === 0) {
          return prev;
        }

        // Fetch definitions for specs that need them

        (async () => {
          for (const spec of specsNeedingDefs) {
            const specId = spec.specificationId || spec._id || spec.id;
            if (!spec.codifyCollection) continue;

            try {
              const response = await get(`https://codify.poc.mrc.ai/api/definitions?collection=${spec.codifyCollection}`);
              const defs = response.data;
              setSpecificationDefinitions(prevMap => {
                const updated = new Map(prevMap);
                updated.set(specId, Array.isArray(defs) ? defs : []);
                return updated;
              });
            } catch (err) {
              console.error(`Failed to fetch definitions for specification ${spec.name}:`, err);
            }
          }
        })();

        return prev;
      });
    };

    if (codifySpecifications.length > 0) {
      fetchSpecDefinitions();
    } else {
      setSpecificationDefinitions(new Map());
    }
  }, [codifySpecifications]);

  useEffect(() => {
    const newEditableMeta = {
      name: documentMeta?.name || "Untitled Document",
      codifyCollection: documentMeta?.codifyCollection || null,
      dataStandard: documentMeta?.dataStandard || null,
      dataType: documentMeta?.dataType || null,
      model: documentMeta?.model || null,
      isTemplate: documentMeta?.isTemplate ?? false,
      productCode: documentMeta?.productCode || null,
    };

    setEditableDocMeta(newEditableMeta);

    // Normalize specs loaded from database to ensure they have specificationId field
    const normalizedSpecs = (documentMeta?.codifySpecifications || []).map(spec => ({
      ...spec,
      specificationId: spec.specificationId || spec._id || spec.id,
    }));

    setCodifySpecifications(normalizedSpecs);

  }, [documentMeta, debugMode]);

  useEffect(() => {
    const currentProvider = providerRef.current;

    if (debugMode && currentProvider) {
    }

    return () => {
      if (currentProvider) {
        currentProvider.destroy();
        providerRef.current = null;
      }
    };
  }, [documentMeta?.documentRepositoryId, debugMode]);

  const getStageDisplayName = (stage) => {
    switch (stage) {
      case "build": return "Build";
      case "quotation": return "Quotation";
      case "order": return "Order";
      case "bound": return "Bound";
      default: return "Build";
    }
  };

  const parsedDoc = React.useMemo(() => {
    if (!tiptapDocument) return null;
    try {
      return typeof tiptapDocument === "string" ? JSON.parse(tiptapDocument) : tiptapDocument;
    } catch (e) {
      if (debugMode) console.error("Failed to parse tiptapDocument", e);
      toast({ title: "Error", description: "Invalid document format.", status: "error", duration: 4000, isClosable: true });
      return null;
    }
  }, [tiptapDocument, toast]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: false,
        history: false, // Disabled - Collaboration extension provides its own history (undo/redo)
        blockquote: {
          HTMLAttributes: {
            class: 'blockquote-content',
          },
        },
      }),
      Paragraph.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            textIndent: {
              default: null,
              parseHTML: element => element.style.textIndent || null,
              renderHTML: attributes => {
                if (!attributes.textIndent) {
                  return {};
                }
                return {
                  style: `text-indent: ${attributes.textIndent}`,
                };
              },
            },
          };
        },
      }),
      PageBreak,
      TextStyle.extend({
        addAttributes() {
          return {
            fontSize: {
              default: "1rem",
              parseHTML: el => el.style.fontSize?.replace(/['"]/g, ""),
              renderHTML: attrs => (attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}),
            },
            lineHeight: {
              default: "normal",
              parseHTML: el => el.style.lineHeight?.replace(/['"]/g, ""),
              renderHTML: attrs => (attrs.lineHeight ? { style: `line-height: ${attrs.lineHeight}` } : {}),
            },
          };
        },
      }),
      FontSize,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      CustomTable.configure({ resizable: true, withHeaderRow: true, handleWidth: 6, allowTableEditing: true }),
      StableRowIds,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'custom-link',
        },
      }),
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      LockManagement.configure({
        userRoles: effectiveUserRoles || [],
        debugMode: debugMode,
      }),
      Collaboration.configure({
        document: ydocRef.current,
      }),
      CollaborationCursor.configure({
        provider: providerRef.current,
        user: {
          name: user?.name || user?.email || 'Anonymous',
          color: generateUserColor(user?.name || user?.email || 'Anonymous'),
        },
      }),
    ],
    editable: true,
  });

  const [cellInfo, setCellInfo] = useState({ current: null, previous: null });
  const [documentLoaded, setDocumentLoaded] = useState(false);

  useEffect(() => {
    if (!editor || !providerRef.current || !parsedDoc) return;

    const provider = providerRef.current;

    const handleSynced = ({ state }) => {
      if (state && ydocRef.current) {
        const fragment = ydocRef.current.getXmlFragment('default');

        if (fragment.length === 0) {
          editor.commands.setContent(parsedDoc);
        } else {
        }
        setDocumentLoaded(true);
      }
    };

    if (provider.synced) {
      handleSynced({ state: true });
    } else {
      provider.on('synced', handleSynced);
    }

    return () => {
      provider.off('synced', handleSynced);
    };
  }, [editor, parsedDoc, debugMode]);

  useEffect(() => {
    if (!editor) return;
    const tr = editor.view.state.tr.setMeta("stampRowIds", true);
    editor.view.dispatch(tr);
  }, [editor]);

  // Load ALL codifications from API on mount (for all participants)
  useEffect(() => {
    if (!documentMeta?.documentRepositoryId) {
      return;
    }

    const loadCodifications = async () => {
      try {
        const repoId = documentMeta.documentRepositoryId;

        // Preserve existing manually-initialized codifications (specs without codificationId yet)
        const specCodifMap = new Map(specificationCodificationsRef.current);
        const specCodifIdMap = new Map(specificationCodificationIds);

        // Use ref to read latest specs without re-triggering this effect
        const specs = codifySpecificationsRef.current;

        // Fetch ALL codifications for this document in a single request
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        const headers = await getHeaders();
        const response = await fetch(
          `${CODIFICATIONS_API}?documentId=${encodeURIComponent(repoId)}`,
          { signal: controller.signal, headers }
        );
        clearTimeout(timeoutId);

        let allCodifications = [];
        if (response.ok) {
          const result = await unwrapFetchResponse(response);
          allCodifications = Array.isArray(result) ? result : [];
        }

        // Index fetched codifications by specificationId for fast lookup
        const codifBySpecId = new Map();
        for (const codif of allCodifications) {
          if (codif.specificationId) {
            codifBySpecId.set(codif.specificationId, codif);
          }
        }

        // Match codifications to specifications
        for (const spec of specs) {
          const specId = spec.specificationId || spec._id || spec.id;
          const codif = codifBySpecId.get(specId);

          if (!codif) {
            // No saved codification for this spec yet
            if (!specCodifMap.has(specId)) {
              specCodifMap.set(specId, new Map());
            }
            continue;
          }

          // Store the codification ID for this specification
          specCodifIdMap.set(specId, codif._id);

          // Build rowData map for this specification
          const specRowMap = new Map();
          if (codif.rows && Array.isArray(codif.rows)) {
            codif.rows.forEach(row => {
              specRowMap.set(row.id, {
                rowId: row.id,
                state: row.state || 'codified',
                inputText: row.content || '',
                output: row.data || '',
                headingType: row.type || row.heading || 'Unknown',
              });
            });
          }
          specCodifMap.set(specId, specRowMap);
        }

        setSpecificationCodifications(specCodifMap);
        setSpecificationCodificationIds(specCodifIdMap);
      } catch (error) {
        if (error.name === 'AbortError') {
          console.error('[CODIFICATION API] Timeout loading codifications');
        } else {
          console.error('[CODIFICATION API] Error loading codifications:', error);
          toast({
            title: 'Failed to Load Codifications',
            description: error.message,
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        }
      }
    };

    loadCodifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentMeta?.documentRepositoryId, debugMode, toast]);

  // Load placement for this document on mount
  const placementLoadedRef = useRef(false);
  useEffect(() => {
    const loadPlacement = async () => {
      if (!documentMeta?.documentRepositoryId) return;
      if (placementLoadedRef.current) return; // Prevent re-fetching

      placementLoadedRef.current = true;
      setIsLoadingPlacement(true);
      try {
        const response = await get(
          `${PLACEMENTS_API}/document/${documentMeta.documentRepositoryId}`
        );

        const data = response.data;
        if (data && data._id) {
          setPlacement(data);
          // Sync placement data with placing state
          if (data.placingStageCode) {
            setPlacingStage(data.placingStageCode);
          }
          if (data.placingTransactionFunctionCode) {
            setPlacingStepCode(data.placingTransactionFunctionCode);
          }
        }
      } catch (error) {
        // 404 is expected when no placement exists for this document
        if (error.response?.status !== 404 && debugMode) {
          console.error('[PLACEMENT API] Error loading placement:', error);
        }
      } finally {
        setIsLoadingPlacement(false);
      }
    };

    loadPlacement();
  }, [documentMeta?.documentRepositoryId, get, debugMode]);

  // Extract organization IDs from JWT token
  const getOrganisationIdsFromToken = useCallback(() => {
    if (!user?.idToken) return [];
    try {
      const parts = user.idToken.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        const rawOrgIds = payload.organisationId;
        if (Array.isArray(rawOrgIds)) {
          return [...new Set(rawOrgIds)];
        } else if (typeof rawOrgIds === 'string') {
          return [rawOrgIds];
        }
      }
    } catch (err) {
      console.error('[PLACEMENT] Error parsing JWT for org IDs:', err);
    }
    return [];
  }, [user?.idToken]);

  // Initiate the start placement flow - check if org selection is needed
  const handleStartPlacement = useCallback(async () => {
    if (!documentMeta?.documentRepositoryId) {
      toast({
        title: 'Cannot Start Placement',
        description: 'Document must be saved first.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const orgIds = getOrganisationIdsFromToken();

    if (orgIds.length === 0) {
      toast({
        title: 'Cannot Start Placement',
        description: 'No organization found for your account.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (orgIds.length === 1) {
      // Only one org, create placement directly
      await createPlacement(orgIds[0]);
    } else {
      // Multiple orgs, show selection modal
      setAvailableOrganisations(orgIds);
      setSelectedOrganisationId(null);

      // Fetch organization names
      const PARTIES_API = "https://directory.poc.mrc.ai/api/parties";
      const orgNamesMap = {};
      try {
        const namePromises = orgIds.map(async (orgId) => {
          try {
            const response = await get(`${PARTIES_API}/${orgId}`);
            orgNamesMap[orgId] = response.data.name || response.data.legalName || orgId;
          } catch (err) {
            orgNamesMap[orgId] = orgId;
          }
        });
        await Promise.all(namePromises);
        setOrganisationNames(orgNamesMap);
      } catch (err) {
        // Fallback to IDs
        orgIds.forEach(id => { orgNamesMap[id] = id; });
        setOrganisationNames(orgNamesMap);
      }

      onOrgSelectOpen();
    }
  }, [documentMeta, getOrganisationIdsFromToken, get, toast, onOrgSelectOpen]);

  // Actually create the placement with a specific organization ID
  const createPlacement = useCallback(async (organisationId) => {
    setIsCreatingPlacement(true);
    try {
      const placementPayload = {
        name: editableDocMeta.name || documentMeta?.name || 'Untitled Placement',
        type: 'open_market', // B35 codeset - default value
        productCode: editableDocMeta.productCode || documentMeta?.productCode || contract?.productCode || '',
        placingStageCode: 'build',
        placingTransactionFunctionCode: 'submission',
        documentId: documentMeta.documentRepositoryId,
        dealId: contract?._id || '',
        programId: '',
        status: 'active',
        organisationOwnerId: organisationId,
      };

      const response = await post(PLACEMENTS_API, placementPayload);

      const newPlacement = response.data;
      setPlacement(newPlacement);
      setPlacingStage('build');
      setPlacingStepCode('submission');

      toast({
        title: 'Placement Started',
        description: 'Document is now in the placing process.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onOrgSelectClose();
    } catch (error) {
      console.error('[PLACEMENT API] Error creating placement:', error);
      toast({
        title: 'Failed to Start Placement',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsCreatingPlacement(false);
    }
  }, [documentMeta, editableDocMeta, contract, post, toast, onOrgSelectClose]);

  // Handle org selection confirmation from modal
  const handleConfirmOrgSelection = useCallback(async () => {
    if (!selectedOrganisationId) {
      toast({
        title: 'Organization Required',
        description: 'Please select an organization for this placement.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    await createPlacement(selectedOrganisationId);
  }, [selectedOrganisationId, createPlacement, toast]);

  // Delete the current placement
  const handleDeletePlacement = useCallback(async () => {
    if (!placement?._id) {
      return;
    }

    setIsDeletingPlacement(true);
    try {
      await del(`${PLACEMENTS_API}/${placement._id}`);

      setPlacement(null);
      setPlacingStage('build');
      setPlacingStepCode('submission');

      toast({
        title: 'Placement Deleted',
        description: 'Document is no longer in the placing process.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('[PLACEMENT API] Error deleting placement:', error);
      toast({
        title: 'Failed to Delete Placement',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsDeletingPlacement(false);
    }
  }, [placement, del, toast]);

  // Sync rowDataMap changes to Yjs for collaboration
  useEffect(() => {
    if (!yCodifiedContentsRef.current) return;

    const yMap = yCodifiedContentsRef.current;

    const handleYChange = () => {
      const updates = {};
      yMap.forEach((value, key) => {
        updates[key] = value;
      });

      setRowDataMap(prev => {
        const newMap = new Map(prev);
        Object.entries(updates).forEach(([rowId, remoteData]) => {
          const local = newMap.get(rowId);
          if (!local || remoteData.version > (local.version || 0)) {
            newMap.set(rowId, remoteData);
          }
        });
        return newMap;
      });
    };

    yMap.observe(handleYChange);

    return () => {
      yMap.unobserve(handleYChange);
    };
  }, [debugMode]);

  // Push rowDataMap changes to Yjs
  useEffect(() => {
    if (!yCodifiedContentsRef.current || rowDataMap.size === 0) return;

    const yMap = yCodifiedContentsRef.current;

    rowDataMap.forEach((rowData, rowId) => {
      const existingEntry = yMap.get(rowId);
      const newVersion = (existingEntry?.version || 0) + 1;

      if (!existingEntry ||
          existingEntry.state !== rowData.state ||
          existingEntry.data !== rowData.data) {


        yMap.set(rowId, {
          ...rowData,
          version: newVersion,
          timestamp: Date.now(),
        });
      }
    });
  }, [rowDataMap, debugMode]);


  // Listen for document updates to detect new rows
  useEffect(() => {
    if (!editor) return;

    let updateTimer = null;

    const handleUpdate = () => {
      if (updateTimer) {
        clearTimeout(updateTimer);
      }

      updateTimer = setTimeout(() => {
        // Build rowDataMap for new rows
        const currentRowIds = new Set();
        editor.state.doc.descendants((node) => {
          if (node.type.name === "tableRow") {
            currentRowIds.add(node.attrs?.id);
          }
          return true;
        });

        const existingIds = new Set(rowDataMapRef.current.keys());
        const hasNewRows = Array.from(currentRowIds).some(id => !existingIds.has(id));

        if (hasNewRows) {

          setRowDataMap(prevMap => {
            const newMap = new Map(prevMap);
            let rowCounter = 0;

            editor.state.doc.descendants((node) => {
              if (node.type.name !== "tableRow") return true;
              const rowId = node.attrs?.id || safeUuid();
              const headingType = node.attrs?.headingType || "Unknown";

              if (!newMap.has(rowId)) {
                // New row - create entry with all metadata

                // Extract content from the content cell (second cell)
                let contentText = "";
                let colIndex = 0;
                node.forEach((cellNode) => {
                  colIndex += 1;
                  if (cellNode.attrs?.role === "content" || colIndex === 2) {
                    contentText = cellNode.textContent?.trim() || "";
                  }
                });

                newMap.set(rowId, {
                  id: rowId,
                  row: rowCounter,
                  col: 1,
                  segment: "tableRow",
                  type: "content",
                  heading: headingType,
                  content: contentText,
                  data: null,
                  state: "new",
                });
              }
              rowCounter += 1;
              return true;
            });

            return newMap;
          });
        }
      }, 500);
    };

    editor.on("update", handleUpdate);
    return () => {
      if (updateTimer) clearTimeout(updateTimer);
      editor.off("update", handleUpdate);
    };
  }, [editor, contract?.productCode, debugMode]);

  // Helper to update codifyStatus attribute on table rows
  const updateRowStatus = useCallback((rowId, status) => {
    if (!editor) {
      return;
    }

    const { state } = editor;
    let tr = state.tr;
    let updated = false;
    let found = false;

    state.doc.descendants((node, pos) => {
      if (node.type.name === "tableRow" && node.attrs?.id === rowId) {
        found = true;
        const currentStatus = node.attrs.codifyStatus;

        if (currentStatus !== status) {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, codifyStatus: status });
          updated = true;
        } else {
        }
      }
      return true;
    });

    if (!found && debugMode) {
      console.error('[UPDATE STATUS] Row not found:', rowId);
    }

    if (updated) {
      editor.view.dispatch(tr);
    }
  }, [editor, debugMode]);

  // Codify document using a specific specification
  // Initialize specification entries from document (no API calls)
  const initializeSpecificationEntries = useCallback((specification) => {
    if (!editor) return;


    const specId = specification.specificationId || specification._id || specification.id;
    const codifiedData = new Map();

    // Get definitions for this specification to filter rows
    const specDefinitions = specificationDefinitionsRef.current.get(specId) || [];

    // Create a set of valid heading types for this specification
    const validHeadingTypes = new Set(
      specDefinitions.map(def => (def.type || def.name)?.toLowerCase()).filter(Boolean)
    );

    // Collect table rows from document that match this specification's definitions
    let totalRows = 0;
    let matchedRows = 0;
    let skippedRows = 0;

    editor.state.doc.descendants((node) => {
      if (node.type.name === "tableRow") {
        totalRows++;
        const rowId = node.attrs?.id;
        const headingType = node.attrs?.headingType;

        // Only include rows whose headingType is in this specification's definitions
        if (rowId && headingType && validHeadingTypes.has(headingType.toLowerCase())) {
          let inputText = "";
          node.forEach(cell => {
            if (cell.textContent) inputText += cell.textContent + " ";
          });
          inputText = inputText.trim();

          if (inputText) {
            // Initialize with 'new' state (no API call yet)
            codifiedData.set(rowId, {
              rowId,
              headingType,
              inputText,
              output: null,
              state: "new",
            });
            matchedRows++;
          }
        } else if (rowId && headingType) {
          skippedRows++;
        }
      }
    });



    // Store entries with 'new' state
    setSpecificationCodifications(prev => {
      const newMap = new Map(prev);
      newMap.set(specId, new Map(codifiedData));
      return newMap;
    });
  }, [editor, debugMode]);

  // Codify all rows for a specific specification
  const codifyAllForSpecification = useCallback(async (specification) => {
    if (!editor) return;


    const specId = specification.specificationId || specification._id || specification.id;
    const specData = specificationCodifications.get(specId);

    if (!specData || specData.size === 0) {
      return;
    }

    const rowsToCodeify = Array.from(specData.values());
    const codifiedData = new Map(specData);

    // Codify each row with the specification's settings
    // Process in batches to balance responsiveness and performance
    const batchSize = 5;
    for (let i = 0; i < rowsToCodeify.length; i++) {
      const { rowId, headingType, inputText } = rowsToCodeify[i];

      try {
        const payload = {
          collection: specification.codifyCollection,
          type: headingType,
          inputText,
          outputDataStandard: specification.dataStandard,
          outputDataType: specification.dataType,
          model: specification.model,
        };

        const codifyHeaders = await getHeaders();
        const res = await fetch(CODIFY_API, {
          method: "POST",
          headers: codifyHeaders,
          body: JSON.stringify(payload),
        });

        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch { data = {}; }

        let codifiedStr = "";
        if (data?.codifiedJSON) {
          if (typeof data.codifiedJSON === "string") {
            try { codifiedStr = JSON.stringify(JSON.parse(data.codifiedJSON), null, 2); }
            catch { codifiedStr = data.codifiedJSON; }
          } else {
            codifiedStr = JSON.stringify(data.codifiedJSON, null, 2);
          }
        }

        if (res.ok && codifiedStr) {
          codifiedData.set(rowId, {
            rowId,
            headingType,
            inputText,
            output: codifiedStr,
            state: "codified",
          });
        } else {
          codifiedData.set(rowId, {
            rowId,
            headingType,
            inputText,
            output: null,
            state: "error",
          });
        }
      } catch (err) {
        console.error(`[SPEC CODIFY ALL] Error codifying row ${rowId}:`, err);
        codifiedData.set(rowId, {
          rowId,
          headingType,
          inputText,
          output: null,
          state: "error",
        });
      }

      // Batch update: update state every batchSize rows or at the end
      if ((i + 1) % batchSize === 0 || i === rowsToCodeify.length - 1) {
        setSpecificationCodifications(prev => {
          const newMap = new Map(prev);
          newMap.set(specId, new Map(codifiedData));
          return newMap;
        });
      }
    }

  }, [editor, debugMode, specificationCodifications]);


  // Replace in-place codify call with queued task
  const enqueueCodify = (rowId, headingType, inputText) => {
    

    // Check which specification definitions contain this heading type
    const codificationsToRun = [];

    // Check each specification's definitions (use refs to get latest values)
    const currentSpecs = codifySpecificationsRef.current;
    const currentSpecDefs = specificationDefinitionsRef.current;
    const currentSpecCodifications = specificationCodificationsRef.current;


    for (const spec of currentSpecs) {
      const specId = spec.specificationId || spec._id || spec.id;
      const specDefs = currentSpecDefs.get(specId) || [];

      const headingInSpec = specDefs.some(def => {
        const match = def.type?.toLowerCase() === headingType?.toLowerCase() ||
                      def.name?.toLowerCase() === headingType?.toLowerCase();
        return match;
      });


      if (headingInSpec) {
        // Always allow re-codification when explicitly triggered (e.g., by editing a cell)
        // The check for existing output was preventing re-codification after the first edit
        codificationsToRun.push({
          type: 'specification',
          specId: specId,
          specName: spec.name,
          collection: spec.codifyCollection,
          dataStandard: spec.dataStandard,
          dataType: spec.dataType,
          model: spec.model,
        });
      }
    }

    // If no specifications matched, cannot codify
    if (codificationsToRun.length === 0) {
      return;
    }
    

    // Mark running in UI
    setRowDataMap(prevMap => {
      const newMap = new Map(prevMap);
      const entry = newMap.get(rowId);
      if (!entry) {
        if (debugMode) console.error('[CODIFY] ERROR: No entry found for rowId:', rowId);
        return prevMap;
      }
      newMap.set(rowId, { ...entry, state: "running" });
      return newMap;
    });

    // Mark all specification codifications as running
    codificationsToRun.forEach((codifConfig) => {
      if (codifConfig.type === 'specification') {
        setSpecificationCodifications(prevMap => {
          const newMap = new Map(prevMap);
          const prevSpecMap = newMap.get(codifConfig.specId);
          const specMap = new Map(prevSpecMap || []);

          // Get existing entry to preserve any existing data
          const existingEntry = specMap.get(rowId);

          // Always mark as running since we're about to codify
          specMap.set(rowId, {
            ...existingEntry,
            rowId,
            state: "running",
            inputText,
            headingType,
          });

          newMap.set(codifConfig.specId, specMap);
          return newMap;
        });
      }
    });
    updateRowStatus(rowId, "running");

    // Execute each codification (specifications only)
    codificationsToRun.forEach((codifConfig) => {
      const queueId = codifConfig.type === 'core'
        ? rowId
        : `${rowId}-spec-${codifConfig.specId}`;

      queueRef.current.enqueue(queueId, async (signal) => {
        const payload = {
          collection: codifConfig.collection,
          type: headingType || "Unknown",
          inputText,
          outputDataStandard: codifConfig.dataStandard,
          outputDataType: codifConfig.dataType,
          model: codifConfig.model,
        };

        const codifyHeaders = await getHeaders();
        const res = await fetch(CODIFY_API, {
          method: "POST",
          headers: codifyHeaders,
          signal,
          body: JSON.stringify(payload),
        });

        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch { data = {}; }

        let codifiedStr = "";
        let hasDefinition = true;

        if (data?.codifiedJSON) {
          if (typeof data.codifiedJSON === "string") {
            try { codifiedStr = JSON.stringify(JSON.parse(data.codifiedJSON), null, 2); }
            catch { codifiedStr = data.codifiedJSON; }
          } else {
            codifiedStr = JSON.stringify(data.codifiedJSON, null, 2);
          }
        }

        // Check if the response indicates no matching definition
        // Common patterns: empty object {}, null, or specific error message
        if (codifiedStr === "{}" || codifiedStr === "null" ||
            data?.message?.toLowerCase().includes("no definition") ||
            data?.message?.toLowerCase().includes("not found")) {
          hasDefinition = false;
        }

        if (!res.ok) {
          const err = new Error(data?.message || "Codification failed");
          err.status = res.status;
          err.retriable = res.status === 429 || res.status >= 500;
          throw err;
        }

        // Success: update state based on codification type
        if (codifConfig.type === 'core') {
          // Core codification - update rowDataMap
          setRowDataMap(prevMap => {
            const newMap = new Map(prevMap);
            const entry = newMap.get(rowId);
            if (entry) {
              newMap.set(rowId, {
                ...entry,
                state: hasDefinition ? "codified" : "no_definition",
                content: inputText,
                data: codifiedStr,
              });
            }
            return newMap;
          });
          updateRowStatus(rowId, hasDefinition ? "done" : "no_definition");
        } else {
          // Specification codification - update specificationCodifications Map
          setSpecificationCodifications(prevMap => {
            const newMap = new Map(prevMap);
            const prevSpecMap = newMap.get(codifConfig.specId);
            const specMap = new Map(prevSpecMap || []);
            specMap.set(rowId, {
              rowId,
              state: hasDefinition ? "codified" : "no_definition",
              inputText,
              headingType,
              output: codifiedStr,
            });
            newMap.set(codifConfig.specId, specMap);
            return newMap;
          });
          // Update visual status indicator in content cell
          updateRowStatus(rowId, "done");
        }

        return true;
      }).promise.catch(err => {
        if (err?.name === "AbortError") return;
        
        if (debugMode) {
          console.error(`[CODIFY] ${codifConfig.type} error for rowId:`, rowId, 
            codifConfig.specName ? `(${codifConfig.specName})` : '', 
            err?.message);
        }

        if (codifConfig.type === 'core') {
          // Mark core error
          setRowDataMap(prevMap => {
            const newMap = new Map(prevMap);
            const entry = newMap.get(rowId);
            if (entry) {
              newMap.set(rowId, { ...entry, state: "error" });
            }
            return newMap;
          });
          updateRowStatus(rowId, "error");
          showApiErrorToast(toast, err, { title: "Codify Error", duration: 3000 });
        } else {
          // Mark specification error
          setSpecificationCodifications(prevMap => {
            const newMap = new Map(prevMap);
            const prevSpecMap = newMap.get(codifConfig.specId);
            const specMap = new Map(prevSpecMap || []);
            specMap.set(rowId, {
              state: "error",
              content: inputText,
              headingType,
            });
            newMap.set(codifConfig.specId, specMap);
            return newMap;
          });
          // Update visual status indicator in content cell
          updateRowStatus(rowId, "error");
          showApiErrorToast(toast, err, { title: `Codify Error (${codifConfig.specName})`, duration: 3000 });
        }
      });
    });
  };

  // Track content changes to mark rows as stale
  useEffect(() => {
    if (!editor) return;

    const lastCodifiedContent = new Map();
    rowDataMapRef.current.forEach((rowData, rowId) => {
      if (rowData.state === "codified" && rowData.content) {
        lastCodifiedContent.set(rowId, rowData.content);
      }
    });

    let contentChangeTimer = null;

    const handleContentChange = () => {
      if (contentChangeTimer) {
        clearTimeout(contentChangeTimer);
      }

      contentChangeTimer = setTimeout(() => {
        const { state } = editor;

        state.doc.descendants((node, pos) => {
          if (node.type.name !== "tableRow") return true;

          const rowId = node.attrs?.id;
          const codifyStatus = node.attrs?.codifyStatus;

          if (!rowId || codifyStatus !== "done") return true;

          let contentText = "";
          node.forEach((cell, offset, index) => {
            if (cell.attrs?.role === "content") {
              contentText = cell.textContent?.trim() || "";
            }
          });

          const lastContent = lastCodifiedContent.get(rowId);
          if (lastContent && contentText && contentText !== lastContent) {
            // Content changed - mark as 'changed'
            setRowDataMap(prevMap => {
              const newMap = new Map(prevMap);
              const entry = newMap.get(rowId);
              if (entry) {
                newMap.set(rowId, { ...entry, state: "changed" });
              }
              return newMap;
            });
            updateRowStatus(rowId, "changed");
            lastCodifiedContent.delete(rowId);
          }

          return true;
        });
      }, 300);
    };

    editor.on("update", handleContentChange);
    return () => {
      if (contentChangeTimer) clearTimeout(contentChangeTimer);
      editor.off("update", handleContentChange);
    };
  }, [editor, updateRowStatus]);

  // Refs for selection listener to avoid re-mounting
  const enqueueCodifyRef = useRef(enqueueCodify);
  const debugModeRef = useRef(debugMode);

  // Keep refs updated
  useEffect(() => {
    enqueueCodifyRef.current = enqueueCodify;
    debugModeRef.current = debugMode;
  }, [enqueueCodify, debugMode]);

  // Selection listener
  useEffect(() => {
    if (!editor) {
      return;
    }


    const lastCellRef = { current: null };
    const lastProcessedTextRef = { current: "" };
    const cellEntryTextMap = { current: new Map() };
    let debounceTimer = null;

    const getTableCtx = ($pos) => {
      let rowDepth = null, cellDepth = null, cellPos = null;
      for (let d = $pos.depth; d > 0; d--) {
        const n = $pos.node(d);
        if ((n.type.name === "tableCell" || n.type.name === "tableHeader") && cellDepth === null) {
          cellDepth = d;
          cellPos = $pos.before(d);
        }
        if (n.type.name === "tableRow" && rowDepth === null) rowDepth = d;
      }
      if (rowDepth == null || cellDepth == null) return null;

      const rowNode = $pos.node(rowDepth);
      const cellNode = $pos.node(cellDepth);
      const rowIndex = $pos.index(rowDepth - 1) + 1;
      const colIndex = $pos.index(rowDepth) + 1;

      const cellAttrs = cellNode.attrs || {};
      const rowAttrs = rowNode.attrs || {};

      const headingType = cellAttrs.headingType ?? rowAttrs.headingType ?? "Unknown";
      let role = cellAttrs.role;
      if (!role) {
        if (colIndex === 1) role = "heading";
        else if (colIndex === 2) role = "content";
        else role = "output";
      }
      const rowId = rowAttrs?.id;

      return {
        rowIndex, colIndex, headingType, role,
        text: cellNode?.textContent?.trim() ?? "",
        rowId,
        cellPos,
      };
    };

    const handleSelectionUpdate = () => {

      if (!editor?.view?.hasFocus?.()) {
        return;
      }

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      debounceTimer = setTimeout(() => {
        processSelectionUpdate();
      }, 300);
    };

    const processSelectionUpdate = () => {
      const debug = debugModeRef.current;

      if (!editor?.view?.hasFocus?.()) {
        return;
      }

      const { selection } = editor.state;

      if (!selection.empty) {
        return;
      }

      const ctxNow = getTableCtx(selection.$from);
      const prevCtx = lastCellRef.current;


      if (prevCtx && ctxNow && prevCtx.cellPos === ctxNow.cellPos) {
        return;
      }

      if (debug && ctxNow && (!prevCtx || prevCtx.cellPos !== ctxNow.cellPos)) {
        setCellInfo(prev => ({
          current: {
            headingType: ctxNow.headingType,
            rowIndex: ctxNow.rowIndex,
            colIndex: ctxNow.colIndex,
            rowId: ctxNow.rowId
          },
          previous: prev.current || prev.previous
        }));
      }

      const enteredContentCell = ctxNow?.role === "content" &&
        (!prevCtx || ctxNow.cellPos !== prevCtx.cellPos);

      if (enteredContentCell) {
        const entryText = ctxNow.text ?? "";
        cellEntryTextMap.current.set(ctxNow.cellPos, entryText);
      }

      lastCellRef.current = ctxNow;

      const leftContentCell = prevCtx?.role === "content" &&
        (!ctxNow || ctxNow.cellPos !== prevCtx.cellPos);


      if (!leftContentCell) return;

      let currentText = "";
      const { state } = editor;
      state.doc.descendants((node, pos) => {
        if (pos === prevCtx.cellPos && (node.type.name === "tableCell" || node.type.name === "tableHeader")) {
          currentText = node.textContent?.trim() ?? "";
          return false;
        }
        return true;
      });

      const cellEntryText = cellEntryTextMap.current.get(prevCtx.cellPos) ?? "";



      if (!currentText || currentText.length <= 3 || currentText === cellEntryText) {
        return;
      }

      lastProcessedTextRef.current = currentText;
      // DO NOT update cellEntryTextMap here - it should only be set when entering the cell
      // so that future edits to the same cell can be detected
      // cellEntryTextMap.current.set(prevCtx.cellPos, currentText);

      const rowId = prevCtx.rowId;
      const headingType = prevCtx.headingType || "Unknown";


      if (!rowId) {
        return;
      }

      enqueueCodifyRef.current(rowId, headingType, currentText);
    };

    editor.on("selectionUpdate", handleSelectionUpdate);

    // ALSO listen to transaction event as fallback (more reliable with Collaboration)
    const handleTransaction = ({ transaction }) => {
      // Only process if selection actually changed
      if (!transaction.selectionSet) return;
      handleSelectionUpdate();
    };

    editor.on("transaction", handleTransaction);

    // When the editor loses focus (user clicks outside), flush any pending
    // content-cell change so codify still fires.
    const handleBlur = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }

      const prevCtx = lastCellRef.current;
      if (!prevCtx || prevCtx.role !== "content") return;

      let currentText = "";
      const { state } = editor;
      state.doc.descendants((node, pos) => {
        if (pos === prevCtx.cellPos && (node.type.name === "tableCell" || node.type.name === "tableHeader")) {
          currentText = node.textContent?.trim() ?? "";
          return false;
        }
        return true;
      });

      const cellEntryText = cellEntryTextMap.current.get(prevCtx.cellPos) ?? "";

      if (!currentText || currentText.length <= 3 || currentText === cellEntryText) {
        lastCellRef.current = null;
        return;
      }

      const rowId = prevCtx.rowId;
      const headingType = prevCtx.headingType || "Unknown";

      if (!rowId) {
        lastCellRef.current = null;
        return;
      }

      lastProcessedTextRef.current = currentText;
      enqueueCodifyRef.current(rowId, headingType, currentText);
      lastCellRef.current = null;
    };

    editor.on("blur", handleBlur);

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      editor.off("selectionUpdate", handleSelectionUpdate);
      editor.off("transaction", handleTransaction);
      editor.off("blur", handleBlur);
    };
  }, [editor]); // Only depend on editor to prevent constant re-mounting

  // Function to re-run codify on current row (used by both Ctrl+R and button click)
  const handleCodifyCurrentRow = useCallback(() => {
    if (!editor) return;

    const { state } = editor;
    const { selection } = state;

    // Find the current table row
    let rowNode = null;
    let rowId = null;
    let headingType = null;

    state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
      if (node.type.name === 'tableRow' && !rowNode) {
        rowNode = node;
        rowId = node.attrs?.id;
        headingType = node.attrs?.headingType;
      }
    });

    if (!rowId || !headingType) {
      toast({
        title: "No row selected",
        description: "Place cursor in a table row to re-run codify",
        status: "info",
        duration: 2000,
      });
      return;
    }

    // Get the content text from the row
    let inputText = "";
    rowNode.forEach(cell => {
      if (cell.textContent) inputText += cell.textContent + " ";
    });
    inputText = inputText.trim();

    if (!inputText || inputText.length <= 3) {
      toast({
        title: "No content",
        description: "Row must have content to codify",
        status: "info",
        duration: 2000,
      });
      return;
    }


    // Clear existing outputs for this row to force re-codification
    setSpecificationCodifications(prev => {
      const newMap = new Map(prev);
      newMap.forEach((specMap, specId) => {
        const entry = specMap.get(rowId);
        if (entry) {
          specMap.set(rowId, {
            ...entry,
            output: null,
            state: "new",
          });
        }
      });
      return newMap;
    });

    // Trigger codify
    setTimeout(() => {
      enqueueCodify(rowId, headingType, inputText);
    }, 100);

    toast({
      title: "Re-running codify",
      description: `Re-codifying ${headingType}`,
      status: "info",
      duration: 2000,
    });
  }, [editor, debugMode, toast, enqueueCodify, setSpecificationCodifications]);

  // Keyboard shortcut: Ctrl+R / Cmd+R to re-run codify on current row
  useEffect(() => {
    if (!editor) return;

    const handleKeyDown = (event) => {
      // Check for Ctrl+R (Windows/Linux) or Cmd+R (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
        event.preventDefault();
        handleCodifyCurrentRow();
      }
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener('keydown', handleKeyDown);

    return () => {
      editorElement.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor, handleCodifyCurrentRow]);

  // Save function that saves to codifications API
  const handleSave = async (isAutoSave = false) => {
    if (!editor) {
      if (debugMode) console.error('[SAVE ERROR] No editor instance available');
      return;
    }
    const repoId = documentMeta?.documentRepositoryId;
    if (!repoId) {
      if (debugMode) console.error('[SAVE ERROR] No documentRepositoryId in documentMeta:', documentMeta);
      if (!isAutoSave) {
        toast({ title: "No linked document", description: "Link a document in the repository before saving.", status: "warning", duration: 3500, isClosable: true });
      }
      return;
    }

    if (isAutoSave) {
      setIsAutoSaving(true);
    }

    const userId = user?._id ||
                   user?.platformUser?._id ||
                   user?.platformUser?.id ||
                   user?.linkedInId ||
                   user?.sub ||
                   user?.email ||
                   null;

    try {
      // ⭐ Save document WITHOUT codifiedContents (just content)
      const documentPayload = {
        definitionId: documentMeta?.definitionId ?? "",
        name: editableDocMeta.name || "Untitled Document",
        content: JSON.stringify(editor.getJSON()),
        // ⭐ NO codifiedContents field anymore
        createdDate: documentMeta?.createdDate ?? new Date().toISOString(),
        updatedBy: userId,
        updatedDate: new Date().toISOString(),
        codifyCollection: editableDocMeta.codifyCollection,
        dataStandard: editableDocMeta.dataStandard,
        dataType: editableDocMeta.dataType,
        model: editableDocMeta.model,
        productCode: editableDocMeta.productCode || documentMeta?.productCode || contract?.productCode,
        isTemplate: editableDocMeta.isTemplate ?? documentMeta?.isTemplate ?? false,
        codifySpecifications: codifySpecifications,
        // Preserve organization ownership (documents without org owner are public/read-only)
        organisationOwnerId: documentMeta?.organisationOwnerId,
      };

      const docRes = await put(`${DOCS_API}/${repoId}`, documentPayload);


      // Save ONLY specification codifications (no MainCodification anymore)
      let updatedCodifySpecs = [...codifySpecifications];

      if (codifySpecifications.length > 0) {

        for (const spec of codifySpecifications) {
          const specId = spec.specificationId || spec._id || spec.id;
          const specCodifMap = specificationCodifications.get(specId);

          // Build rows array from specification codifications (may be empty for newly added specs)
          const specRows = [];
          let rowIdx = 0;
          
          // Get segment info and heading text from the actual table rows
          const rowMetadataMap = new Map();
          editor.state.doc.descendants((node) => {
              if (node.type.name === "tableRow" && node.attrs?.id) {
                const segmentName = node.attrs?.segmentName || node.attrs?.segmentType || "Unknown";
                let headingText = "";
                let colIndex = 0;
                node.forEach((cellNode) => {
              colIndex += 1;
              if (cellNode.attrs?.role === "heading" || colIndex === 1) {
                headingText = cellNode.textContent?.trim() || "";
              }
            });
            rowMetadataMap.set(node.attrs.id, { segmentName, headingText });
          }
          });
          
          for (const [rowId, rowData] of (specCodifMap || new Map()).entries()) {
            const metadata = rowMetadataMap.get(rowId) || { segmentName: "Unknown", headingText: "" };
            specRows.push({
              id: rowId,
              row: rowIdx,
              col: 1,
              segment: metadata.segmentName,
              type: rowData.headingType || "Unknown",
              heading: metadata.headingText,
              content: rowData.inputText || rowData.content || "",
              data: rowData.output || rowData.data || "",
              state: rowData.state || "new",
            });
            rowIdx++;
          }

          const specCodificationPayload = {
            documentId: repoId,
            participantId: userId,
            participantRole: selectedParticipantRole,
            specificationId: specId,
            specificationName: spec.name,
            collection: spec.codifyCollection,
            type: "SpecificationCodification",
            outputDataStandard: spec.dataStandard,
            outputDataType: spec.dataType,
            model: spec.model,
            output: null,
            state: "codified",
            rows: specRows,
            createdBy: userId,
            updatedBy: userId,
            organisationOwnerId: documentMeta?.organisationOwnerId,
          };


          // Check if we have an existing codification ID for this specification
          // First check the spec itself, then fall back to in-memory map
          const existingCodifId = spec.codificationId || specificationCodificationIds.get(specId);
          const specCodifUrl = existingCodifId
            ? `${CODIFICATIONS_API}/${existingCodifId}`
            : CODIFICATIONS_API;
          const specCodifMethod = existingCodifId ? 'PUT' : 'POST';


          try {
            let specCodifRes;
            if (specCodifMethod === 'POST') {
              specCodifRes = await post(specCodifUrl, specCodificationPayload);
            } else {
              specCodifRes = await put(specCodifUrl, specCodificationPayload);
            }

            const savedSpecCodif = unwrapApiResponse(specCodifRes.data);

            if (savedSpecCodif) {
              // Update the local copy of codifySpecifications with the codificationId
              updatedCodifySpecs = updatedCodifySpecs.map(s => {
                const sId = s.specificationId || s._id || s.id;
                return sId === specId
                  ? { ...s, codificationId: savedSpecCodif._id }
                  : s;
              });

              // Store the codification ID for future updates
              setSpecificationCodificationIds(prev => {
                const newMap = new Map(prev);
                newMap.set(specId, savedSpecCodif._id);
                return newMap;
              });

            }
          } catch (err) {
            console.error('[SAVE ERROR] Specification codification save failed:', {
              specName: spec.name,
              error: err.message,
              response: err.response?.data
            });
            // Don't throw - continue saving other specs
          }
        }
        
        // Update state with all codificationIds
        setCodifySpecifications(updatedCodifySpecs);
        
        // Save document again with updated codifySpecifications including codificationIds
        
        const updatedDocPayload = {
          ...documentPayload,
          codifySpecifications: updatedCodifySpecs,
        };

        try {
          await put(`${DOCS_API}/${repoId}`, updatedDocPayload);
        } catch (err) {
          console.error('[SAVE ERROR] Failed to save document with updated codification IDs:', err);
          if (!isAutoSave) {
            toast({
              title: 'Partial Save Warning',
              description: 'Codifications were saved but the document reference could not be updated. Please save again.',
              status: 'warning',
              duration: 5000,
              isClosable: true,
            });
          }
        }
      }

      if (!isAutoSave) {
        showApiSuccessToast(toast, docRes, { title: "Document Saved", fallbackMessage: "Your changes have been saved" });
      }

      setHasUnsavedChanges(false);
      setLastSavedAt(new Date());
      setIsAutoSaving(false);

      if (onSave) {
        onSave(documentPayload);
      }
    } catch (err) {
      if (debugMode) console.error('[SAVE ERROR] Exception during save:', err);
      setIsAutoSaving(false);
      if (!isAutoSave) {
        showApiErrorToast(toast, err, { title: "Save Failed" });
      }
    }
  };

  // Auto-save with debouncing - saves 5 seconds after last change
  useEffect(() => {
    if (!editor || !hasUnsavedChanges) return;

    const autoSaveTimer = setTimeout(() => {
      if (hasUnsavedChanges) {
        handleSave(true); // isAutoSave = true
      }
    }, 5000); // 5 seconds debounce

    return () => clearTimeout(autoSaveTimer);
  }, [hasUnsavedChanges, editor, debugMode, handleSave]);

  // Track changes to mark document as unsaved
  useEffect(() => {
    if (!editor) return;

    const handleUpdate = () => {
      setHasUnsavedChanges(true);
    };

    editor.on('update', handleUpdate);
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      window.history.back();
    }
  };

  const handleOpenMetadataEditor = useCallback((rowId) => {
    if (!editor) return;

    const { state } = editor;
    let rowMetadata = null;

    state.doc.descendants((node, pos) => {
      if (node.type.name === "tableRow" && node.attrs?.id === rowId) {
        rowMetadata = {
          id: rowId,
          segmentType: node.attrs?.segmentType || "",
          segmentName: node.attrs?.segmentName || "",
          fieldId: node.attrs?.fieldId || "",
          headingType: node.attrs?.headingType || "",
          editableBy: node.attrs?.editableBy || [],
          adminBy: node.attrs?.adminBy || [],
        };
        return false;
      }
      return true;
    });

    if (rowMetadata) {
      setSelectedRowMetadata(rowMetadata);
      setIsMetadataEditorOpen(true);
    }
  }, [editor]);

  const handleSaveMetadata = useCallback((updatedMetadata) => {
    if (!editor || !selectedRowMetadata) return;

    const { state } = editor;
    let tr = state.tr;
    let updated = false;

    state.doc.descendants((node, pos) => {
      if (node.type.name === "tableRow" && node.attrs?.id === selectedRowMetadata.id) {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          segmentType: updatedMetadata.segmentType,
          segmentName: updatedMetadata.segmentName,
          fieldId: updatedMetadata.fieldId,
          headingType: updatedMetadata.headingType,
          editableBy: updatedMetadata.editableBy,
          adminBy: updatedMetadata.adminBy,
        });
        updated = true;
        return false;
      }
      return true;
    });

    if (updated) {
      editor.view.dispatch(tr);
    }
  }, [editor, selectedRowMetadata]);

  const handleStatusDotClick = useCallback((rowId) => {

    setDrawerOpen(prev => {
      const wasOpen = prev;

      // Find index in document order
      let index = -1;
      let counter = 0;
      editor.state.doc.descendants((node) => {
        if (node.type.name === "tableRow") {
          if (node.attrs?.id === rowId) {
            index = counter;
            return false;
          }
          counter++;
        }
        return true;
      });

      if (index === -1) return prev;

      setExpandedAccordionIndices(prevIndices => {
        if (prevIndices.includes(index)) {
          return prevIndices;
        }
        const newIndices = [...prevIndices, index];
        return newIndices;
      });

      setTimeout(() => {
        const accordionItem = document.querySelector(`[data-codify-id="${rowId}"]`);
        if (accordionItem) {
          accordionItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, wasOpen ? 0 : 300);

      return true;
    });
  }, [editor, debugMode]);

  useEffect(() => {
    if (!editor) return;

    const handleClick = (event) => {
      const firstCell = event.target.closest('td:first-child, th:first-child');
      if (firstCell) {
        const rect = firstCell.getBoundingClientRect();
        const clickX = event.clientX;
        const clickY = event.clientY;

        const iconLeft = rect.left - 28;
        const iconRight = rect.left;
        const iconTop = rect.top;
        const iconBottom = rect.top + 32;

        const isInIconArea = clickX >= iconLeft && clickX <= iconRight &&
                            clickY >= iconTop && clickY <= iconBottom;

        if (isInIconArea) {
          const row = event.target.closest('tr[data-id]');
          if (row) {
            const rowId = row.getAttribute('data-id');
            if (rowId) {
              event.preventDefault();
              event.stopPropagation();
              handleOpenMetadataEditor(rowId);
              return;
            }
          }
        }
      }

      const cell = event.target.closest('td:nth-child(2), th:nth-child(2), td[data-role="content"], th[data-role="content"]');
      if (!cell) return;

      const rect = cell.getBoundingClientRect();
      const clickX = event.clientX;
      const clickY = event.clientY;

      const dotLeft = rect.right - 24;
      const dotTop = rect.top + (rect.height / 2) - 12;
      const dotBottom = rect.top + (rect.height / 2) + 12;

      if (clickX >= dotLeft && clickX <= rect.right &&
          clickY >= dotTop && clickY <= dotBottom) {
        const row = event.target.closest('tr[data-id]');
        if (!row) {
          return;
        }

        const rowId = row.getAttribute('data-id');
        if (rowId) {
          event.preventDefault();
          event.stopPropagation();
          handleStatusDotClick(rowId);
        }
      }
    };

    const editorEl = editor.view.dom;
    editorEl.addEventListener('click', handleClick, true);

    return () => {
      editorEl.removeEventListener('click', handleClick, true);
    };
  }, [editor, handleStatusDotClick, handleOpenMetadataEditor]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const containerWidth = window.innerWidth;
      const newWidth = ((containerWidth - e.clientX) / containerWidth) * 100;
      setPanelWidth(Math.min(Math.max(newWidth, 15), 50));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Build display array for accordion - merge rowDataMap AND specificationCodifications
  const displayRows = React.useMemo(() => {
    const rows = [];
    editor?.state.doc.descendants((node) => {
      if (node.type.name === "tableRow") {
        const rowId = node.attrs?.id;
        if (!rowId) return true;

        // Check rowDataMap first (core codifications)
        const rowData = rowDataMap.get(rowId);
        if (rowData) {
          rows.push({
            id: rowId,
            type: rowData.heading || node.attrs?.headingType || "Unknown",
            state: rowData.state || "new",
            output: rowData.data,
          });
          return true; // Continue to next row
        }

        // If not in rowDataMap, check specification codifications
        let foundInSpec = false;
        specificationCodifications.forEach((specRowMap, specId) => {
          const specRowData = specRowMap.get(rowId);
          if (specRowData && !foundInSpec) {
            rows.push({
              id: rowId,
              type: specRowData.headingType || node.attrs?.headingType || "Unknown",
              state: specRowData.state || "codified",
              output: specRowData.output,
            });
            foundInSpec = true;
          }
        });

        // If not in either, create a placeholder entry
        if (!foundInSpec) {
          rows.push({
            id: rowId,
            type: node.attrs?.headingType || "Unknown",
            state: "new",
            output: null,
          });
        }
      }
      return true;
    });
    return rows;
  }, [editor, rowDataMap, specificationCodifications]);

  return (
    <Box height="100vh" display="flex" flexDirection="column" overflow="hidden">
      <Navbar />

      <DocumentMetadataEditor
        isOpen={isMetadataEditorOpen}
        onClose={() => setIsMetadataEditorOpen(false)}
        rowData={selectedRowMetadata}
        userRoles={effectiveUserRoles}
        onSave={handleSaveMetadata}
      />

      <Box position="fixed" width="100%" height="100%" top={0} left={0} zIndex={0}>
        <Image src={HeroImage} alt="Hero Watermark" width="100%" height="100%" objectFit="cover" opacity={0.35} filter="blur(1px)" />
      </Box>

      <Box position="relative" zIndex={1} p={10} flex="1" display="flex" flexDirection="column" overflow="hidden">

        <Box borderWidth="1px" borderRadius="md" p={3} mb={2} borderColor="gray.500" backgroundColor="whiteAlpha.900" flexShrink={0}>
          <HStack spacing={6} align="flex-start" w="full">
            <Tooltip label="Back to the Product Dashboard">
              <IconButton icon={<HiOutlineArrowNarrowLeft />} size="md" onClick={handleClose} />
            </Tooltip>
            <Input
              value={editableDocMeta.name}
              onChange={(e) =>
                setEditableDocMeta((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              size="lg"
              fontSize="2xl"
              fontWeight="bold"
              variant="unstyled"
              placeholder="Document Name"
              _placeholder={{ color: "gray.400" }}
              flex="1"
              maxW="1000px"
            />
            {documentMeta?.isTemplate && (
              <Box
                px={3}
                py={1}
                borderRadius="md"
                bg="blue.100"
                borderWidth="1px"
                borderColor="blue.300"
                fontSize="sm"
                fontWeight="semibold"
                color="blue.700"
                whiteSpace="nowrap"
              >
                Template
              </Box>
            )}
            <Box ml="auto" display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
              <HStack spacing={2}>
                <Button
                  leftIcon={<HiOutlinePrinter />}
                  colorScheme="gray"
                  onClick={async () => {
                    const repoId = documentMeta?.documentRepositoryId;
                    if (!repoId) {
                      toast({
                        title: 'Error',
                        description: 'No document ID found',
                        status: 'error',
                        duration: 3000,
                      });
                      return;
                    }

                    toast({
                      title: 'Generating PDF...',
                      description: 'Please wait while we create your PDF',
                      status: 'info',
                      duration: 2000,
                    });

                    try {
                      const headers = await getHeaders();
                      const response = await fetch(`${DOCS_API}/${repoId}/render/pdf`, {
                        method: 'POST',
                        headers
                      });

                      if (!response.ok) {
                        throw new Error(`Failed to generate PDF: ${response.statusText}`);
                      }

                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${editableDocMeta.name || 'document'}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);

                      toast({
                        title: 'PDF Generated!',
                        description: `Downloaded as ${a.download}`,
                        status: 'success',
                        duration: 3000,
                      });
                    } catch (error) {
                      console.error('PDF generation error:', error);

                      // Check for 401 unauthorized
                      if (error.response?.status === 401) {
                        toast({
                          title: 'Session Expired',
                          description: 'Your session has expired. Please refresh the page to log in again.',
                          status: 'error',
                          duration: 10000,
                          isClosable: true,
                        });
                      } else {
                        toast({
                          title: 'Error generating PDF',
                          description: error.message,
                          status: 'error',
                          duration: 5000,
                        });
                      }
                    }
                  }}
                  size="md"
                >
                  Export PDF
                </Button>
                <Button
                  leftIcon={<HiOutlineSave />}
                  colorScheme="blue"
                  onClick={handleSave}
                  size="md"
                >
                  Save
                </Button>
              </HStack>
              <VStack spacing={0} align="flex-end">
                {isAutoSaving && (
                  <Text fontSize="xs" color="blue.500" fontWeight="medium">
                    Auto-saving...
                  </Text>
                )}
                {!isAutoSaving && lastSavedAt && (
                  <Text fontSize="xs" color="gray.500">
                    Last saved: {lastSavedAt.toLocaleTimeString()}
                  </Text>
                )}
                {hasUnsavedChanges && !isAutoSaving && (
                  <Text fontSize="xs" color="orange.500">
                    Unsaved changes
                  </Text>
                )}
                <HStack spacing={1} align="center">
                  <Text fontSize="xs" color="gray.600">
                    Queue: {queueRef.current.size}
                  </Text>
                  <QueueMonitor queueRef={queueRef} />
                </HStack>
              </VStack>
            </Box>
          </HStack>
        </Box>

        {debugMode && (
          <Box bg="yellow.100" border="1px solid" borderColor="yellow.400" p={2} mb={2} borderRadius="md" flexShrink={0}>
            <Text as="strong" fontSize="xs">Current Cell:</Text>{" "}
            {cellInfo.current ? `${cellInfo.current.headingType || "N/A"} | Row: ${cellInfo.current.rowIndex ?? "?"} | Col: ${cellInfo.current.colIndex ?? "?"} | RowID: ${cellInfo.current.rowId ? cellInfo.current.rowId.substring(0, 8) + '...' : 'N/A'}` : "None"}
            <br />
            <Text as="strong" fontSize="xs">Previous Cell:</Text>{" "}
            {cellInfo.previous ? `${cellInfo.previous.headingType || "N/A"} | Row: ${cellInfo.previous.rowIndex ?? "?"} | Col: ${cellInfo.previous.colIndex ?? "?"} | RowID: ${cellInfo.previous.rowId ? cellInfo.previous.rowId.substring(0, 8) + '...' : 'N/A'}` : "None"}
          </Box>
        )}

        <Box
          className="mrc-editor"
          display="flex"
          flexDirection="column"
          borderWidth="1px"
          borderRadius="md"
          borderColor="gray.500"
          backgroundColor="whiteAlpha.900"
          flex="1"
          overflow="hidden"
        >
          <PlacingProcessBar
            currentStage={placingStage}
            currentStepCode={placingStepCode}
            participantRole={participantRole}
            placement={placement}
            isLoadingPlacement={isLoadingPlacement}
            isCreatingPlacement={isCreatingPlacement}
            isDeletingPlacement={isDeletingPlacement}
            onStartPlacement={handleStartPlacement}
            onDeletePlacement={handleDeletePlacement}
            onStepChange={(stage, stepCode) => {
              setPlacingStage(stage);
              setPlacingStepCode(stepCode);
            }}
            onStageChange={(stage, stepCode) => {
              setPlacingStage(stage);
              setPlacingStepCode(stepCode);
              toast({
                title: `Switched to ${getStageDisplayName(stage)} Stage`,
                status: "info",
                duration: 2000,
                isClosable: true
              });
            }}
          />

          <Box borderBottom="1px" borderColor="gray.300" p={2} backgroundColor="whiteAlpha.900" flexShrink={0}>
            <MenuBar
              editor={editor}
              onSave={handleSave}
              onClose={handleClose}
              onShowCodified={() => setDrawerOpen(!isDrawerOpen)}
              onCodifyCurrentRow={handleCodifyCurrentRow}
              debugMode={debugMode}
              onToggleDebug={() => setDebugMode(!debugMode)}
              isSuperAdmin={isSuperAdmin}
              codifyCollection={editableDocMeta.codifyCollection}
              documentDefinitionId={documentMeta?.definitionId}
            />
          </Box>

          <HStack flex="1" spacing={0} alignItems="stretch" overflow="hidden">
            <Box
              flex="1"
              overflowY="auto"
              overflowX="hidden"
              w={isDrawerOpen ? `${100 - panelWidth}%` : "100%"}
            >
              <div className="editor-wrapper" style={{ zoom }}>
                <div
                  className="editor-page"
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (editor && !editor.state.selection.empty) {
                      // If text is already selected, BubbleMenu will show automatically
                      return;
                    }
                    // If no selection, select the word at cursor position
                    if (editor) {
                      const { from } = editor.state.selection;
                      const $pos = editor.state.doc.resolve(from);
                      const word = editor.state.doc.textBetween(
                        Math.max(0, $pos.before()),
                        Math.min(editor.state.doc.content.size, $pos.after())
                      );
                      if (word) {
                        editor.commands.setTextSelection({
                          from: $pos.before(),
                          to: $pos.after()
                        });
                      }
                    }
                  }}
                >
                  <EditorContent editor={editor} />
                  {editor && (
                    <BubbleMenu
                      editor={editor}
                      tippyOptions={{
                        duration: 100,
                        placement: 'top-start',
                        offset: [0, -10], // [horizontal, vertical] - negative to move closer/overlap
                        appendTo: () => document.body,
                      }}
                      shouldShow={({ editor, view, state, oldState, from, to }) => {
                        // Don't show if link input is active
                        if (showLinkInput) return false;
                        // Only show if there's an actual selection
                        return from !== to;
                      }}
                    >
                      <HStack
                        bg="white"
                        color="gray.800"
                        px={2}
                        py={1}
                        borderRadius="md"
                        boxShadow="lg"
                        border="1px solid"
                        borderColor="gray.200"
                        spacing={1}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <IconButton
                          size="xs"
                          icon={<strong>B</strong>}
                          onClick={() => editor.chain().focus().toggleBold().run()}
                          bg={editor.isActive('bold') ? 'blue.100' : 'transparent'}
                          color={editor.isActive('bold') ? 'blue.600' : 'gray.700'}
                          _hover={{ bg: editor.isActive('bold') ? 'blue.200' : 'gray.100' }}
                          aria-label="Bold"
                        />
                        <IconButton
                          size="xs"
                          icon={<em>I</em>}
                          onClick={() => editor.chain().focus().toggleItalic().run()}
                          bg={editor.isActive('italic') ? 'blue.100' : 'transparent'}
                          color={editor.isActive('italic') ? 'blue.600' : 'gray.700'}
                          _hover={{ bg: editor.isActive('italic') ? 'blue.200' : 'gray.100' }}
                          aria-label="Italic"
                        />
                        <IconButton
                          size="xs"
                          icon={<u>U</u>}
                          onClick={() => editor.chain().focus().toggleUnderline().run()}
                          bg={editor.isActive('underline') ? 'blue.100' : 'transparent'}
                          color={editor.isActive('underline') ? 'blue.600' : 'gray.700'}
                          _hover={{ bg: editor.isActive('underline') ? 'blue.200' : 'gray.100' }}
                          aria-label="Underline"
                        />
                        <IconButton
                          size="xs"
                          icon={<s>S</s>}
                          onClick={() => editor.chain().focus().toggleStrike().run()}
                          bg={editor.isActive('strike') ? 'blue.100' : 'transparent'}
                          color={editor.isActive('strike') ? 'blue.600' : 'gray.700'}
                          _hover={{ bg: editor.isActive('strike') ? 'blue.200' : 'gray.100' }}
                          aria-label="Strikethrough"
                        />
                        <Select
                          size="xs"
                          w="70px"
                          bg="white"
                          color="gray.800"
                          borderColor="gray.300"
                          _hover={{ bg: 'gray.50' }}
                          onChange={(e) => editor.chain().focus().setFontSize(e.target.value).run()}
                          value={editor.getAttributes('textStyle').fontSize || '1rem'}
                        >
                          <option value="0.5rem">8</option>
                          <option value="0.625rem">10</option>
                          <option value="0.75rem">12</option>
                          <option value="0.875rem">14</option>
                          <option value="1rem">16</option>
                          <option value="1.125rem">18</option>
                          <option value="1.25rem">20</option>
                          <option value="1.5rem">24</option>
                          <option value="1.75rem">28</option>
                          <option value="2rem">32</option>
                        </Select>

                        <Box w="1px" h="20px" bg="gray.300" />

                        {/* Link Controls */}
                        {showLinkInput ? (
                          <HStack spacing={1}>
                            <Input
                              size="xs"
                              w="150px"
                              bg="white"
                              color="gray.800"
                              borderColor="gray.300"
                              placeholder="Enter URL"
                              value={linkUrl}
                              onChange={(e) => setLinkUrl(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  if (linkUrl) {
                                    editor.chain().focus().setLink({ href: linkUrl }).run();
                                  }
                                  setShowLinkInput(false);
                                  setLinkUrl('');
                                }
                                if (e.key === 'Escape') {
                                  setShowLinkInput(false);
                                  setLinkUrl('');
                                }
                              }}
                              autoFocus
                            />
                            <IconButton
                              size="xs"
                              icon={<SmallCloseIcon />}
                              onClick={() => {
                                setShowLinkInput(false);
                                setLinkUrl('');
                              }}
                              bg="transparent"
                              color="gray.700"
                              _hover={{ bg: 'gray.100' }}
                              aria-label="Cancel"
                            />
                          </HStack>
                        ) : (
                          <IconButton
                            size="xs"
                            icon={<HiOutlineLink />}
                            onClick={() => {
                              if (editor.isActive('link')) {
                                editor.chain().focus().unsetLink().run();
                              } else {
                                const previousUrl = editor.getAttributes('link').href;
                                setLinkUrl(previousUrl || '');
                                setShowLinkInput(true);
                              }
                            }}
                            bg={editor.isActive('link') ? 'blue.100' : 'transparent'}
                            color={editor.isActive('link') ? 'blue.600' : 'gray.700'}
                            _hover={{ bg: editor.isActive('link') ? 'blue.200' : 'gray.100' }}
                            aria-label="Link"
                          />
                        )}

                        <Box w="1px" h="20px" bg="gray.300" />

                        {/* Clear Formatting */}
                        <IconButton
                          size="xs"
                          icon={<MdFormatColorReset />}
                          onClick={() => {
                            editor.chain().focus().clearNodes().unsetAllMarks().run();
                          }}
                          bg="transparent"
                          color="gray.700"
                          _hover={{ bg: 'gray.100' }}
                          aria-label="Clear Formatting"
                        />
                      </HStack>
                    </BubbleMenu>
                  )}
                </div>
              </div>
            </Box>

            {isDrawerOpen && (
              <>
                <Box
                  w="4px"
                  h="100%"
                  bg="gray.300"
                  cursor="col-resize"
                  _hover={{ bg: "blue.400" }}
                  transition="background-color 0.2s"
                  onMouseDown={handleMouseDown}
                  userSelect="none"
                />
                <Box
                  w={`${panelWidth}%`}
                  pt={2}
                  px={2}
                  pb={2}
                  bg="white"
                  display="flex"
                  flexDirection="column"
                  overflow="hidden"
                  zIndex={10}
                  borderLeft="1px solid"
                  borderColor="gray.300"
                >
                  <HStack justify="space-between" mb={1} flexShrink={0}>
                    <Heading size="sm">Codified Data</Heading>
                    <HStack spacing={1}>
                      <Tooltip label="Zoom Out JSON">
                        <IconButton
                          icon={<HiOutlineMinus />}
                          size="xs"
                          onClick={() => setJsonZoom((prev) => Math.max(prev - 0.1, 0.5))}
                        />
                      </Tooltip>
                      <Text fontSize="xs" minW="40px" textAlign="center">
                        {Math.round(jsonZoom * 100)}%
                      </Text>
                      <Tooltip label="Zoom In JSON">
                        <IconButton
                          icon={<HiOutlinePlus />}
                          size="xs"
                          onClick={() => setJsonZoom((prev) => Math.min(prev + 0.1, 2.0))}
                        />
                      </Tooltip>
                      <IconButton icon={<HiOutlineArrowNarrowLeft />} aria-label="Close Codified" onClick={() => setDrawerOpen(false)} size="sm" />
                    </HStack>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" fontStyle="italic" textTransform="uppercase" mb={2} flexShrink={0}>
                    Codified data has been generated by AI
                  </Text>

                  <Box
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={2}
                    mb={2}
                    flexShrink={0}
                    bg="gray.50"
                  >
                    <HStack justify="space-between" mb={0}>
                      <Text fontSize="xs" fontWeight="semibold">Codify Specifications</Text>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<HiOutlinePlus />}
                          size="xs"
                          aria-label="Add specification"
                          variant="outline"
                        />
                        <MenuList maxH="300px" overflowY="auto">
                          {availableSpecifications.map(spec => {
                            const specId = spec.specificationId || spec._id || spec.id;
                            const alreadyAdded = codifySpecifications.some(cs => {
                              const csId = cs.specificationId || cs._id || cs.id;
                              return csId === specId;
                            });

                            return (
                              <MenuItem
                                key={spec.specificationId}
                                fontSize="sm"
                                isDisabled={alreadyAdded}
                                onClick={async () => {
                                  try {

                                    // Add specification - include both 'id' and 'specificationId' for compatibility
                                    const normalizedSpecId = spec.specificationId || spec._id || spec.id;
                                    const newSpec = {
                                      id: normalizedSpecId,
                                      specificationId: normalizedSpecId,
                                      name: spec.name,
                                      ownerPartyId: spec.ownerPartyId,
                                      codifyCollection: spec.codifyCollection,
                                      dataStandard: spec.dataStandard,
                                      dataType: spec.dataType,
                                      modelProvider: spec.modelProvider,
                                      model: spec.model,
                                    };


                                    // Fetch definitions for this specification first
                                    try {
                                      const defRes = await get(`https://codify.poc.mrc.ai/api/definitions?collection=${newSpec.codifyCollection}`);
                                      {
                                        const defsArray = Array.isArray(defRes.data) ? defRes.data : [];

                                        // IMPORTANT: Add spec to codifySpecifications FIRST
                                        // This prevents the useEffect from overwriting our manually-set definitions
                                        setCodifySpecifications(prev => {
                                          const updated = [...prev, newSpec];
                                          return updated;
                                        });

                                        // Update specificationDefinitions with the new spec's definitions
                                        // Do this AFTER adding to codifySpecifications to preserve existing specs' definitions
                                        setSpecificationDefinitions(prev => {
                                          const newMap = new Map(prev);
                                          newMap.set(newSpec.id, defsArray);
                                          return newMap;
                                        });

                                        // Wait for refs to update, then initialize entries
                                        setTimeout(() => {
                                          initializeSpecificationEntries(newSpec);
                                        }, 100);
                                      }
                                    } catch (err) {
                                      console.error('[SPEC ADD] Failed to load definitions:', err);
                                    }
                                  } catch (error) {
                                    console.error('[SPEC ADD] Error:', error);
                                  }
                                }}
                              >
                                {spec.name} ({spec.codifyCollection})
                              </MenuItem>
                            );
                          })}
                        </MenuList>
                      </Menu>
                    </HStack>
                  </Box>

                  <Box flex="1" overflow="hidden" display="flex" flexDirection="column">
                    <Tabs size="sm" variant="enclosed" display="flex" flexDirection="column" flex="1" overflow="hidden">
                      <TabList flexShrink={0}>
                        {codifySpecifications.map((spec, index) => {
                          const specId = spec.specificationId || spec._id || spec.id;
                          return (
                            <Tab key={specId}>{index + 1}</Tab>
                          );
                        })}
                      </TabList>
                      <TabPanels flex="1" overflowY="auto" overflowX="hidden">
                        {/* Specification Tabs */}
                        {codifySpecifications.map(spec => {
                          // Normalize specificationId (support id, _id, and specificationId fields)
                          const specId = spec.specificationId || spec._id || spec.id;
                          const specCodifications = specificationCodifications.get(specId);

                          // Get document order from editor
                          const documentOrder = [];
                          if (editor) {
                            editor.state.doc.descendants((node) => {
                              if (node.type.name === "tableRow" && node.attrs?.id) {
                                documentOrder.push(node.attrs.id);
                              }
                            });
                          }

                          // Create rows from codifications
                          const specRows = specCodifications ? Array.from(specCodifications.entries()).map(([rowId, data]) => ({
                            id: rowId,
                            type: data.headingType || 'Unknown',
                            state: data.state || 'pending',
                            output: data.output || data.data || null,
                          })) : [];

                          // Sort rows by document order
                          specRows.sort((a, b) => {
                            const indexA = documentOrder.indexOf(a.id);
                            const indexB = documentOrder.indexOf(b.id);

                            // If row not in document, put at end
                            if (indexA === -1) return 1;
                            if (indexB === -1) return -1;

                            return indexA - indexB;
                          });

                          return (
                            <TabPanel key={specId} p={0} pt={2}>
                              <HStack justify="space-between" mb={2} px={2}>
                                <Text fontSize="sm" fontWeight="bold" color="gray.700">{spec.name}</Text>
                                <HStack spacing={1}>
                                  <Button
                                    size="xs"
                                    colorScheme="blue"
                                    variant="ghost"
                                    onClick={() => codifyAllForSpecification(spec)}
                                  >
                                    Codify All
                                  </Button>
                                  <IconButton
                                    icon={<HiOutlineTrash />}
                                    size="xs"
                                    variant="ghost"
                                    aria-label="Remove specification"
                                    onClick={async () => {
                                      if (window.confirm(`Are you sure you want to remove "${spec.name}"?`)) {
                                        setCodifySpecifications(prev => prev.filter(s => {
                                          const sId = s.specificationId || s._id || s.id;
                                          return sId !== specId;
                                        }));
                                        // Clean up associated codification state
                                        setSpecificationCodifications(prev => {
                                          const newMap = new Map(prev);
                                          newMap.delete(specId);
                                          return newMap;
                                        });
                                        setSpecificationCodificationIds(prev => {
                                          const newMap = new Map(prev);
                                          newMap.delete(specId);
                                          return newMap;
                                        });
                                        // Delete codification document from API if it exists
                                        const codifId = spec.codificationId || specificationCodificationIds.get(specId);
                                        if (codifId) {
                                          try {
                                            const headers = await getHeaders();
                                            await fetch(`${CODIFICATIONS_API}/${codifId}`, { method: 'DELETE', headers });
                                          } catch (err) {
                                            console.error('[DELETE] Failed to delete codification:', err);
                                          }
                                        }
                                      }
                                    }}
                                    _hover={{ color: "red.500", bg: "transparent" }}
                                  />
                                </HStack>
                              </HStack>
                              {specRows.length === 0 ? (
                                <Text fontSize="sm" color="gray.500" p={4} textAlign="center">
                                  No codifications yet
                                </Text>
                              ) : (
                                <Accordion allowMultiple>
                                  {specRows.map(row => (
                                    <AccordionItem key={row.id} border="1px solid" borderColor="gray.200" borderRadius="md" mb={2}>
                                      <AccordionButton>
                                        <Box flex="1" textAlign="left">
                                          <HStack justify="space-between">
                                            <Text fontWeight="semibold" fontSize="sm">{row.type}</Text>
                                            <Text fontSize="xs" color={
                                              row.state === "running" ? "blue.600" :
                                              row.state === "changed" ? "orange.600" :
                                              row.state === "error" ? "red.600" :
                                              row.state === "codified" ? "green.600" :
                                              row.state === "no_definition" ? "yellow.700" : "gray.600"
                                            }>
                                              {row.state === "no_definition" ? "No Definition" : row.state}
                                            </Text>
                                          </HStack>
                                        </Box>
                                        <AccordionIcon />
                                      </AccordionButton>
                                      <AccordionPanel pb={2} fontSize="xs">
                                        {row.output ? (
                                          (() => {
                                            try {
                                              const parsed = JSON.parse(row.output);
                                              return (
                                                <ReactJson
                                                  src={parsed}
                                                  theme="rjv-default"
                                                  collapsed={1}
                                                  displayDataTypes={false}
                                                  displayObjectSize={false}
                                                  enableClipboard={true}
                                                  name={false}
                                                  indentWidth={2}
                                                  style={{ 
                                                    fontSize: `${11 * jsonZoom}px`, 
                                                    backgroundColor: "white" 
                                                  }}
                                                />
                                              );
                                            } catch {
                                              return <Text fontSize="xs" color="gray.600">{row.output}</Text>;
                                            }
                                          })()
                                        ) : (
                                          <Text fontSize="xs" color="gray.500">No output yet</Text>
                                        )}
                                      </AccordionPanel>
                                    </AccordionItem>
                                  ))}
                                </Accordion>
                              )}
                            </TabPanel>
                          );
                        })}
                      </TabPanels>
                    </Tabs>
                  </Box>
                </Box>
              </>
            )}
          </HStack>

          <Box p={2} borderTop="1px" borderColor="gray.300" backgroundColor="whiteAlpha.900" flexShrink={0}>
            <HStack spacing={4} justify="center">
              <Tooltip label="Zoom Out">
                <IconButton icon={<HiOutlineMinus />} size="sm" onClick={() => setZoom((prev) => Math.max(prev - 0.1, 0.5))} />
              </Tooltip>
              <Slider aria-label="Zoom Slider" value={zoom * 100} min={50} max={200} onChange={(val) => setZoom(val / 100)} width="200px">
                <SliderTrack><SliderFilledTrack /></SliderTrack>
                <SliderThumb />
              </Slider>
              <Text fontSize="sm" minW="50px" textAlign="center">{Math.round(zoom * 100)}%</Text>
              <Tooltip label="Zoom In">
                <IconButton icon={<HiOutlinePlus />} size="sm" onClick={() => setZoom((prev) => Math.min(prev + 0.1, 2))} />
              </Tooltip>
            </HStack>
          </Box>
        </Box>
      </Box>

      {/* Organization Selection Modal for Placement */}
      <Modal isOpen={isOrgSelectOpen} onClose={onOrgSelectClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select Organization</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Select which organization should own this placement.
              </Text>
              <FormControl isRequired>
                <FormLabel>Organization</FormLabel>
                <Select
                  placeholder="Select organization..."
                  value={selectedOrganisationId || ""}
                  onChange={(e) => setSelectedOrganisationId(e.target.value)}
                >
                  {availableOrganisations.map((orgId) => (
                    <option key={orgId} value={orgId}>
                      {organisationNames[orgId] || orgId}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onOrgSelectClose} isDisabled={isCreatingPlacement}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirmOrgSelection}
              isLoading={isCreatingPlacement}
              loadingText="Starting..."
              isDisabled={!selectedOrganisationId}
            >
              Start Placement
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
